import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API, toUrlSlug } from '../utils/api';

// Create Footer Context
const FooterContext = createContext({
  footerData: { countries: [], wineTypes: [], regions: [], appellations: [], varieties: [] },
  refreshFooterData: () => {}
});

// Custom hook to use footer context
export const useFooterData = () => {
  const context = useContext(FooterContext);
  if (!context) {
    throw new Error('useFooterData must be used within FooterProvider');
  }
  return context;
};

// Helper function to fetch footer data from wine deals
export const fetchFooterData = async () => {
  try {
    const response = await axios.get(`${API}/wine-deals?limit=500&fields=card`);
    if (response.data.success && response.data.data) {
      const deals = response.data.data;
      
      // Extract unique countries, wine types, regions, appellations, and varieties
      const countries = [...new Set(deals.map(deal => deal.country).filter(Boolean))];
      const wineTypes = [...new Set(deals.map(deal => deal.wine_type).filter(Boolean))];
      const regions = [...new Set(deals.map(deal => deal.region).filter(Boolean))];
      const appellations = [...new Set(deals.map(deal => deal.appellation).filter(Boolean))];
      const varieties = [...new Set(deals.map(deal => deal.variety).filter(Boolean))];
      
      // Helper function to convert wine type to URL-friendly path
      const wineTypeToPath = (wineType) => {
        const typeMap = {
          'Red wine': 'red-wine',
          'White wine': 'white-wine',
          'Rosé wine': 'rose-wine',
          'Rose wine': 'rose-wine',
          'Sparkling wine': 'sparkling-wine',
          'White sparkling': 'white-sparkling',
          'Rose sparkling': 'rose-sparkling'
        };
        return typeMap[wineType] || toUrlSlug(wineType);
      };
      
      // Custom display order for wine types (instead of alphabetical)
      const wineTypeOrder = [
        'Red wine',
        'White wine',
        'Rosé wine',
        'Rose wine',
        'Sparkling wine',
        'White sparkling',
        'Rose sparkling',
      ];

      // Always include the aggregate "Sparkling wine" entry (routes to /type/sparkling-wine,
      // which combines both "White sparkling" and "Rose sparkling").
      const wineTypesWithAggregate = wineTypes.includes('Sparkling wine')
        ? wineTypes
        : [...wineTypes, 'Sparkling wine'];

      const orderedWineTypes = wineTypesWithAggregate.sort((a, b) => {
        const ai = wineTypeOrder.indexOf(a);
        const bi = wineTypeOrder.indexOf(b);
        // Known types first in custom order; unknowns fall to the end, alphabetically.
        if (ai === -1 && bi === -1) return a.localeCompare(b);
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });

      return {
        countries: [...countries].sort(),
        wineTypes: orderedWineTypes.map(wineType => ({
          name: wineType,
          path: wineTypeToPath(wineType)
        })),
        regions: [...regions].sort().map(region => ({
          name: region,
          path: toUrlSlug(region)
        })),
        appellations: [...appellations].sort().map(appellation => ({
          name: appellation,
          path: toUrlSlug(appellation)
        })),
        varieties: [...varieties].sort().map(variety => ({
          name: variety,
          path: toUrlSlug(variety)
        }))
      };
    }
  } catch (error) {
    console.error('Error fetching footer data:', error);
  }
  
  return { countries: [], wineTypes: [], regions: [], appellations: [], varieties: [] };
};

// Footer Provider Component
export const FooterProvider = ({ children }) => {
  const [footerData, setFooterData] = useState({ 
    countries: [], 
    wineTypes: [], 
    regions: [], 
    appellations: [], 
    varieties: [] 
  });

  const refreshFooterData = async () => {
    const data = await fetchFooterData();
    setFooterData(data);
  };

  useEffect(() => {
    refreshFooterData();
  }, []);

  return (
    <FooterContext.Provider value={{ footerData, refreshFooterData }}>
      {children}
    </FooterContext.Provider>
  );
};

export default FooterContext;
